/**
 * Singleton QueryClient + helper to wrap the app in a provider.
 *
 * Defaults chosen for the v2 use case:
 *   - staleTime: 30s. Queue counts + CAP lists tolerate ~30s stale
 *     data without confusing the operator; chips that need fresher
 *     data can pass `staleTime: 0` per-query.
 *   - retry: 1. ApiError 4xx shouldn't retry (it's a permission /
 *     not-found problem, not a flake); retry: 1 with the predicate
 *     below skips retries on 4xx and gives 5xx one shot.
 *   - refetchOnWindowFocus: true. The operator alt-tabs between
 *     MARSAD + the regulator portal a lot; a focus-refetch keeps
 *     the queue counts current.
 *
 * The query key shape follows the standard react-query pattern of
 * `[domain, entity, ...args]` so invalidation can be coarse
 * (invalidateQueries(["caps"]) after a workflow transition) without
 * tracking every key by hand.
 */
import { MutationCache, QueryClient } from "@tanstack/react-query";

import { ApiError } from "shared/api/client";

/** A 429 is transient, so it retries - but boundedly, not forever. */
const MAX_RATE_LIMIT_RETRIES = 3;
/** Ceiling on any single backoff wait, server-supplied or computed. */
const MAX_RETRY_DELAY_MS = 30_000;
/** Round 17 K18 -- how often a failed read asks again; CardError says it. */
export const ERROR_REFETCH_MS = 30_000;

/**
 * A1 -- a write that fails must say so.
 *
 * The round 6 walk found "Create draft" on the MVB declarations
 * register posting, receiving a 403, and showing nothing at all: no
 * toast, no error, no change on screen. A governance write failing in
 * silence is the class this programme has spent six rounds closing.
 *
 * It is not one component's bug. Forty-seven mutations across the SPA
 * handle `onSuccess` and nothing else, because react-query makes
 * success the thing you write and failure the thing you forget. So the
 * DEFAULT changes rather than forty-seven call sites: a mutation whose
 * error nobody handles surfaces one, and a component that wants
 * bespoke handling still overrides it.
 *
 * The sink is registered by the ToastProvider, the same indirection
 * `setAuthTokenGetter` already uses -- a module-level client cannot
 * call a hook, and inverting it would make the toast a dependency of
 * the data layer.
 */
// ROUND 15 A10 -- the error travels with the message, so the sink can
// tell a refusal (which carries a catalogue code and its own sentence)
// from a write that did not save. Additive: a sink that reads only the
// message still works.
type FailureSink = (message: string, error?: unknown) => void;

let _failureSink: FailureSink | null = null;

/** Register where an unhandled mutation failure should be announced. */
export function setMutationFailureSink(sink: FailureSink): void {
  _failureSink = sink;
}

function announceFailure(error: unknown): void {
  // No sink means no UI is mounted (tests, SSR). Say nothing rather
  // than throw inside react-query's own error path.
  if (!_failureSink) return;
  const message =
    error instanceof ApiError
      ? error.detail || error.message
      : error instanceof Error
        ? error.message
        : String(error);
  _failureSink(message, error);
}

export const queryClient = new QueryClient({
  mutationCache: new MutationCache({
    onError: (error, _vars, _ctx, mutation) => {
      // `onError` on the mutation itself is the component saying it
      // will handle this. Anything else is a failure nobody is
      // showing, which is what this exists to catch.
      if (mutation.options.onError) return;
      // E1 (round 8) -- and a try/catch around `mutateAsync` is the
      // component saying exactly the same thing, which react-query
      // cannot see: `mutateAsync` rejects so the caller CAN catch, and
      // nothing distinguishes a caught rejection from an ignored one.
      //
      // So the caller says so. Seven sites handled their own failures
      // this way and got two toasts in two wordings -- the server's
      // detail from here and their own message from the catch -- which
      // on a Test of Design failure showed an auditor the same reason
      // twice, differently worded.
      //
      // `meta` rather than a convention about naming, because it is
      // declared beside the handling and a ratchet can check it.
      if (mutation.options.meta?.["handled"]) return;
      announceFailure(error);
    },
    /* Round 14 F2 -- the setup ladder answers "is the bank set up?",
       and almost any write can move a rung: a connector enabled or
       failing, branding saved, a declaration signed. Home said "Your
       setup is done" for up to a minute with a connector failing,
       because each surface was left to remember to refresh it and most
       did not. So every successful write refreshes it, here, once. The
       two queries are small, and a missed one is the defect. */
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: onboardingKeys.all });
      void queryClient.invalidateQueries({ queryKey: journeySpineKeys.all });
      // Round 17 R4 -- the thread is derived from every ceremony, and
      // straight after a counter-signature the strip read "awaiting
      // signature" beside a page reading "signed". Any write moves it.
      void queryClient.invalidateQueries({ queryKey: ["thread"] });
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: true,
      // Round 17 K18 -- a read that failed after its retries asks again
      // on a stated interval, and the card says so (CardError), rather
      // than reading "Couldn't load" until the reader reloads.
      refetchInterval: (query) =>
        query.state.status === "error" ? ERROR_REFETCH_MS : false,
      retry: (failureCount, error) => {
        if (error instanceof ApiError) {
          // 429 is the ONE 4xx that is transient. The server is saying
          // "slow down", not "no" - and it says for how long. Treating
          // it like a 403 meant a rate-limited request failed instantly
          // with no backoff, so every component remount re-fired at full
          // speed and made the rate limiting worse. That is not a lab
          // artefact: a bank behind a single egress NAT puts its whole
          // staff on one source IP, which is exactly the shape that
          // trips a per-IP limit.
          if (error.status === 429) {
            return failureCount < MAX_RATE_LIMIT_RETRIES;
          }
          // Round 17 K18 -- a 503 is "busy, ask again" with its wait,
          // the same instruction as a 429.
          if (error.status === 503) {
            return failureCount < MAX_RATE_LIMIT_RETRIES;
          }
          // Every other 4xx is a permission / not-found / bad-request -
          // retrying won't change the answer.
          if (error.status >= 400 && error.status < 500) {
            return false;
          }
        }
        // 5xx + network errors get one shot.
        return failureCount < 1;
      },
      retryDelay: (failureCount, error) => {
        // Honour Retry-After when the server sent one (ApiError already
        // parses it, P61.2). Capped, because an uncapped server-supplied
        // wait becomes an indefinite hang the user cannot distinguish
        // from a broken app.
        if (error instanceof ApiError && error.retryAfterSeconds !== undefined) {
          return Math.min(error.retryAfterSeconds * 1000, MAX_RETRY_DELAY_MS);
        }
        return Math.min(1000 * 2 ** failureCount, MAX_RETRY_DELAY_MS);
      },
    },
    mutations: {
      // Mutations don't retry by default - a failed POST should
      // surface to the operator immediately, not silently retry.
      retry: false,
    },
  },
});

// ── Query key namespaces ────────────────────────────────────────────
// Centralised here so invalidation calls reference the same constants
// the hooks register under. Mutations call e.g.
// queryClient.invalidateQueries({ queryKey: capsKeys.all }) and every
// list/detail/counts query refreshes in one go.

export const regimeKeys = {
  all: ["regimes"] as const,
  one: (code: string) => ["regimes", code] as const,
};

export const meKeys = {
  all: ["me"] as const,
};

/** P106: onboarding / Getting Started status. */
export const onboardingKeys = {
  all: ["onboarding-status"] as const,
};

// P274: the lifecycle-journey spine status (distinct from journeyKeys,
// which is the twin customer-journeys catalogue).
export const journeySpineKeys = {
  all: ["journey-spine-status"] as const,
};

export const lettersKeys = {
  all: ["letters"] as const,
  list: () => [...lettersKeys.all, "list"] as const,
  detail: (id: string) => [...lettersKeys.all, "detail", id] as const,
};

export const capsKeys = {
  all: ["caps"] as const,
  counts: () => [...capsKeys.all, "counts"] as const,
  list: (position?: string) =>
    position
      ? ([...capsKeys.all, "list", position] as const)
      : ([...capsKeys.all, "list"] as const),
  detail: (id: string) => [...capsKeys.all, "detail", id] as const,
  entries: (capId: string) => [...capsKeys.all, "entries", capId] as const,
  controls: (capId: string) => [...capsKeys.all, "controls", capId] as const,
  findings: (capId: string) => [...capsKeys.all, "findings", capId] as const,
  entryControls: (capId: string, entryId: string) =>
    [...capsKeys.all, "entry-controls", capId, entryId] as const,
};

// Framework assessments (P10).
export const frameworkAssessmentKeys = {
  all: ["framework-assessments"] as const,
  frameworks: () => [...frameworkAssessmentKeys.all, "frameworks"] as const,
  catalogue: (code: string) =>
    [...frameworkAssessmentKeys.all, "catalogue", code] as const,
  cycles: (framework?: string) =>
    framework
      ? ([...frameworkAssessmentKeys.all, "cycles", framework] as const)
      : ([...frameworkAssessmentKeys.all, "cycles"] as const),
  cycleAssessments: (cycleId: string) =>
    [...frameworkAssessmentKeys.all, "assessments", cycleId] as const,
  evidence: (assessmentId: string) =>
    [...frameworkAssessmentKeys.all, "evidence", assessmentId] as const,
  analyserRuns: (cycleId: string) =>
    [...frameworkAssessmentKeys.all, "analyser-runs", cycleId] as const,
};

/**
 * Cascade view query key namespace (P2.6).
 *
 * One key per (as_of, severities, limit) tuple so changing any of
 * those creates a fresh cache entry. When ?as_of= changes (P2.8
 * AsOfPicker), the query key shifts and React Query refetches without
 * the operator triggering a manual reload.
 */
export const cascadeKeys = {
  all: ["cascade"] as const,
  view: (params: {
    as_of?: string | undefined;
    severities?: readonly string[] | undefined;
    limit?: number | undefined;
  }) =>
    [
      ...cascadeKeys.all,
      "view",
      params.as_of ?? null,
      params.severities ? [...params.severities].sort().join(",") : null,
      params.limit ?? null,
    ] as const,
};

/**
 * Capability map query key namespace (P2.7).
 *
 * One key per as_of value so the AsOfPicker (P2.8) can flip the
 * timestamp and React Query refetches the new state cleanly.
 */
export const capabilityMapKeys = {
  all: ["capability-map"] as const,
  view: (params: { as_of?: string | undefined }) =>
    [...capabilityMapKeys.all, "view", params.as_of ?? null] as const,
};

/**
 * Landscape view query key namespace (P6).
 *
 * Keyed by (catalog_code, as_of, confidence_min, overlay) so any
 * toolbar change shifts the query key cleanly. Tile-detail drawer has
 * its own sub-namespace keyed by tile_id + as_of.
 */
/**
 * MVB declarations query key namespace (P7).
 *
 * Active declaration + scorecard share the cache so a refetch of
 * one invalidates the other. Per-declaration detail uses its own
 * sub-key so editing a draft doesn't churn the active card.
 */
export const mvbKeys = {
  all: ["mvb"] as const,
  active: () => [...mvbKeys.all, "active"] as const,
  scorecard: () => [...mvbKeys.all, "scorecard"] as const,
  list: (params?: { status?: string | undefined }) =>
    [...mvbKeys.all, "list", params?.status ?? null] as const,
  detail: (id: string) => [...mvbKeys.all, "detail", id] as const,
  /** Round 14 C3 -- the one-declaration slot, under the same root so
   *  every declaration write refreshes every surface that asks. */
  slot: () => [...mvbKeys.all, "slot"] as const,
};

/**
 * Channel heatmap query key namespace (P8).
 *
 * Keyed by as_of so AsOfPicker rewinding refetches cleanly.
 */
export const channelHeatmapKeys = {
  all: ["channel-heatmap"] as const,
  view: (params: { as_of?: string | undefined }) =>
    [...channelHeatmapKeys.all, "view", params.as_of ?? null] as const,
};

/**
 * Customer journeys query key namespace (P16.2 / #485).
 *
 * Separate top-level namespace because the public read shapes
 * (JourneySummary + JourneyDetail) come from `twin/journey_router.py`,
 * NOT from foundation admin. Frontend admin uses the
 * `foundation-admin/journeys` key family in
 * `useFoundationAdminList`.
 */
export const journeyKeys = {
  all: ["journeys"] as const,
  list: (params: { ibs_service_id?: string | undefined }) =>
    [...journeyKeys.all, "list", params.ibs_service_id ?? null] as const,
  detail: (id: string) => [...journeyKeys.all, "detail", id] as const,
};

/**
 * Board snapshot query key namespace (P17 / #458).
 *
 * Keyed by as_of so AsOfPicker rewinding refetches cleanly. The
 * JSON snapshot is the truth; the PDF endpoint is server-rendered
 * + returned via a `<a>` download link (no React Query cache for
 * the PDF byte stream).
 */
export const boardKeys = {
  all: ["board"] as const,
  snapshot: (params: { as_of?: string | undefined }) =>
    [...boardKeys.all, "snapshot", params.as_of ?? null] as const,
};

/** Stitching review 5.4 -- the posture, keyed by as_of like the board. */
export const postureKeys = {
  all: ["posture"] as const,
  at: (as_of: string | undefined) => [...postureKeys.all, as_of ?? null] as const,
};

export const landscapeKeys = {
  all: ["landscape"] as const,
  view: (params: {
    catalogCode: string;
    as_of?: string | undefined;
    confidence_min?: string | undefined;
    overlay?: string | undefined;
  }) =>
    [
      ...landscapeKeys.all,
      "view",
      params.catalogCode,
      params.as_of ?? null,
      params.confidence_min ?? null,
      params.overlay ?? null,
    ] as const,
  tile: (params: { tileId: string; as_of?: string | undefined }) =>
    [...landscapeKeys.all, "tile", params.tileId, params.as_of ?? null] as const,
};

/**
 * Per-entity findings query key namespace (P2.7).
 *
 * The entity detail pages (P2.7.e) and the CellDrawer (P2.7.d) both
 * consume this hook; keying by (entity_kind, entity_id, status,
 * severities, limit) means each surface has its own cache slot.
 */
export const findingsKeys = {
  all: ["findings"] as const,
  forEntity: (params: {
    entity_kind: string;
    entity_id: string;
    status?: string | undefined;
    severities?: readonly string[] | undefined;
    limit?: number | undefined;
  }) =>
    [
      ...findingsKeys.all,
      params.entity_kind,
      params.entity_id,
      params.status ?? null,
      params.severities ? [...params.severities].sort().join(",") : null,
      params.limit ?? null,
    ] as const,
};

/**
 * Per-entity basic detail query key namespace (P2.7).
 *
 * One key per (kind, id) so the 5 entity detail pages each get their
 * own cache slot. `useEntityDetail` consumes this.
 */
export const entityKeys = {
  all: ["entity"] as const,
  detail: (kind: string, id: string) => [...entityKeys.all, kind, id] as const,
};

/**
 * Per-entity OBLIGATION snapshot query key namespace (P102).
 *
 * One key per (kind, id). Powers the "my obligations" panel on the
 * entity detail pages -- which regulatory clauses apply to this entity.
 */
export const obligationKeys = {
  all: ["obligation-snapshot"] as const,
  detail: (kind: string, id: string) => [...obligationKeys.all, kind, id] as const,
};

/**
 * Obligation TRACEABILITY query key namespace (P105). One key per
 * obligation id -- the controls that implement it + their assessment.
 */
export const obligationTraceabilityKeys = {
  all: ["obligation-traceability"] as const,
  detail: (obligationId: string) =>
    [...obligationTraceabilityKeys.all, obligationId] as const,
};

/**
 * Chain DIFF query key namespace (P2.8).
 *
 * One key per (chain, kind, id, from, to). Different from/to anchors
 * produce different cache slots so the user can swap dates without
 * the previous result lingering.
 */
/**
 * Chain TREND query key namespace (P2.9).
 *
 * One key per (chain, window, entity_kind). Default window of 30
 * means the Home ResilienceTrendCard uses the same cache slot as
 * any other 30-day query against the same chain.
 */
export const chainTrendKeys = {
  all: ["chain-trend"] as const,
  query: (params: {
    chain: string;
    window: number;
    entity_kind?: string | undefined;
  }) =>
    [
      ...chainTrendKeys.all,
      params.chain,
      params.window,
      params.entity_kind ?? null,
    ] as const,
};

export const chainDiffKeys = {
  all: ["chain-diff"] as const,
  query: (params: {
    chain: string;
    entity_kind: string;
    entity_id: string;
    from: string;
    to: string;
  }) =>
    [
      ...chainDiffKeys.all,
      params.chain,
      params.entity_kind,
      params.entity_id,
      params.from,
      params.to,
    ] as const,
};

// ── Constellation Twin (P83) ──────────────────────────────────────────

export const twinGraphKeys = {
  all: ["twin-graph"] as const,
  query: (params: { as_of: string | null }) =>
    [...twinGraphKeys.all, params.as_of] as const,
};

export const twinBlastKeys = {
  all: ["twin-blast"] as const,
  query: (params: { kind: string; id: string; as_of: string | null }) =>
    [...twinBlastKeys.all, params.kind, params.id, params.as_of] as const,
  history: (params: { kind: string; id: string; limit: number }) =>
    [...twinBlastKeys.all, "history", params.kind, params.id, params.limit] as const,
};

export const provenanceKeys = {
  all: ["provenance"] as const,
  query: (params: {
    chain: string;
    entity_kind: string;
    entity_id: string;
    as_of: string | null;
  }) =>
    [
      ...provenanceKeys.all,
      params.chain,
      params.entity_kind,
      params.entity_id,
      params.as_of,
    ] as const,
};

// ── Capability map graph (P85.2) ──────────────────────────────────────

export const capabilityGraphKeys = {
  all: ["capability-graph"] as const,
  query: (params: { as_of: string | null }) =>
    [...capabilityGraphKeys.all, params.as_of] as const,
};

// ── MVB candidate selection (P112) ────────────────────────────────────

export const mvbCandidatesKeys = {
  all: ["mvb-candidates"] as const,
  list: () => [...mvbCandidatesKeys.all, "list"] as const,
  detail: (id: string) => [...mvbCandidatesKeys.all, "detail", id] as const,
  convergence: () => [...mvbCandidatesKeys.all, "convergence"] as const,
};

// ── Per-user preferences (P115) ───────────────────────────────────────

export const userPreferencesKeys = {
  all: ["user-preferences"] as const,
};

// ── Attestation programme (P131) ──────────────────────────────────────

export const attestationKeys = {
  all: ["attestations"] as const,
  cycles: () => [...attestationKeys.all, "cycles"] as const,
  cycle: (id: string) => [...attestationKeys.all, "cycle", id] as const,
  list: (cycleId?: string) =>
    cycleId
      ? ([...attestationKeys.all, "list", cycleId] as const)
      : ([...attestationKeys.all, "list"] as const),
  ibsOptions: () => [...attestationKeys.all, "ibs-options"] as const,
};

// ── Resilience drills (P132) ──────────────────────────────────────────

export const drillKeys = {
  all: ["drills"] as const,
  list: (ibsId?: string) =>
    ibsId
      ? ([...drillKeys.all, "list", ibsId] as const)
      : ([...drillKeys.all, "list"] as const),
  detail: (id: string) => [...drillKeys.all, "detail", id] as const,
};

// ── Notifications (P133) ──────────────────────────────────────────────

export const notificationKeys = {
  all: ["notifications"] as const,
  feed: () => [...notificationKeys.all, "feed"] as const,
  unreadCount: () => [...notificationKeys.all, "unread-count"] as const,
};
